import React, { useLayoutEffect, useRef } from 'react';
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { PartnrSendButton } from './PartnrSendButton';

const PILL_WIDTH = 280;
const BOX_WIDTH = 360;
const LINE_HEIGHT = 20;
const MAX_LINES = 5;

interface PartnrComposeBoxProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  placeholder: string;
  isStreaming: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Always-visible trailing control, e.g. the chat history button. */
  trailing?: React.ReactNode;
  /** Shown at the top of the open box: selected items, suggestions. */
  header?: React.ReactNode;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

/**
 * Partnr input that sits in the toolbar as a pill and stretches upward into
 * a multi-line compose box while focused. The pill's footprint never changes,
 * so the toolbar doesn't shift when the box opens.
 */
export function PartnrComposeBox({
  value,
  onChange,
  onKeyDown,
  onSubmit,
  placeholder,
  isStreaming,
  expanded,
  onExpandedChange,
  trailing,
  header,
  textareaRef,
}: PartnrComposeBoxProps) {
  const ownRef = useRef<HTMLTextAreaElement>(null);
  const ref = textareaRef ?? ownRef;
  const hasText = value.trim().length > 0;
  // MotionConfig only covers transforms; the width spring needs its own check
  const reduceMotion = useReducedMotion();

  // Grow with the text up to MAX_LINES, then scroll. Measured on change only.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = expanded ? LINE_HEIGHT * MAX_LINES : LINE_HEIGHT;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, [value, expanded, ref]);

  return (
    <MotionConfig reducedMotion="user">
      {/* Keeps the pill's footprint in the toolbar while the box floats above */}
      <div className="relative h-9" style={{ width: PILL_WIDTH }}>
        <motion.div
          data-expanded={expanded || undefined}
          initial={false}
          animate={{ width: expanded ? BOX_WIDTH : PILL_WIDTH }}
          transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 40 }}
          onBlur={(e) => {
            // The open box covers the first few toolbar tools, so collapse as
            // soon as focus leaves it. The draft stays in the pill.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              onExpandedChange(false);
            }
          }}
          className={cn(
            'absolute bottom-0 left-0 z-10 flex flex-col overflow-hidden transition-[border-radius,background-color,box-shadow] duration-200',
            expanded
              ? 'rounded-2xl bg-popover border border-border shadow-lg'
              : 'rounded-full bg-partnr/5',
          )}
        >
          <AnimatePresence initial={false}>
            {expanded && header && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="px-3 pt-3"
              >
                {header}
              </motion.div>
            )}
          </AnimatePresence>

          <div className={cn('flex items-end gap-1 pl-3 pr-2', expanded ? 'py-2' : 'h-9 items-center')}>
            <textarea
              ref={ref}
              rows={1}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={() => onExpandedChange(true)}
              placeholder={placeholder}
              aria-label="Ask Partnr"
              className={cn(
                'flex-1 min-w-0 resize-none bg-transparent text-sm leading-5 outline-none',
                'placeholder:text-muted-foreground/50',
                !expanded && 'overflow-hidden whitespace-nowrap',
              )}
            />

            <div className="flex items-center gap-1 shrink-0">
              {trailing}
              <PartnrSendButton hasText={hasText} isStreaming={isStreaming} onSubmit={onSubmit} />
            </div>
          </div>
        </motion.div>
      </div>
    </MotionConfig>
  );
}
